import { apiClient } from "../../lib/apiClient";
import { ProjectListItem } from "../auth/useAuth";

export interface CreateProjectInput {
  name: string;
  icon_base64?: string;
}

/**
 * The project as POST /projects returns it — the detail shape, not the list
 * shape: it carries the icon blob and members_count, and none of the
 * list-only aggregates (has_icon, players_count).
 */
export interface CreatedProject {
  id: string;
  name: string;
  icon_base64?: string | null;
  etag: string;
  status: "active" | "suspended";
  created_at: string;
  last_activity_at: string | null;
  members_count: number;
}

export interface CreateProjectResponse {
  project: CreatedProject;
  api_key_plaintext: string;
}

/** Stored project lifecycle status. "setup" is a derived UI-only state. */
export type ProjectStatus = "active" | "suspended";

export interface ProjectDetail {
  id: string;
  name: string;
  icon_base64?: string | null;
  etag: string;
  /** Stored lifecycle status — "active" or "suspended". */
  status: ProjectStatus;
  created_at: string;
  last_activity_at: string | null;
  members_count: number;
  /** Raw project config JSON object. May be absent on legacy responses. */
  config?: unknown;
  /** The admin who created the project — null if unset; email null if deleted. */
  created_by: { id: string; email: string | null } | null;
  /** Number of outstanding (not-yet-accepted) member invites. */
  pending_invites_count: number;
}

export interface PatchProjectInput {
  name?: string;
  icon_base64?: string | null;
  config?: unknown;
}

export async function listProjects(): Promise<ProjectListItem[]> {
  const r = await apiClient.get<{ projects: ProjectListItem[] }>(
    "/api/admin/projects",
  );
  return r.data.projects;
}

export async function createProject(
  input: CreateProjectInput,
): Promise<CreateProjectResponse> {
  const r = await apiClient.post<CreateProjectResponse>(
    "/api/admin/projects",
    input,
  );
  return r.data;
}

export async function getProject(
  id: string,
): Promise<{ data: ProjectDetail; etag: string | null }> {
  return apiClient.get<ProjectDetail>(
    `/api/admin/projects/${encodeURIComponent(id)}`,
  );
}

export async function patchProject(
  id: string,
  input: PatchProjectInput,
  etag: string,
): Promise<{ data: ProjectDetail; etag: string | null }> {
  return apiClient.patch<ProjectDetail>(
    `/api/admin/projects/${encodeURIComponent(id)}`,
    input,
    etag,
  );
}

/** Suspend a project — super_admin only. Returns the updated project. */
export async function suspendProject(id: string): Promise<ProjectDetail> {
  const r = await apiClient.post<ProjectDetail>(
    `/api/admin/projects/${encodeURIComponent(id)}/suspend`,
  );
  return r.data;
}

/** Unsuspend (reactivate) a project — super_admin only. Returns the updated project. */
export async function unsuspendProject(id: string): Promise<ProjectDetail> {
  const r = await apiClient.post<ProjectDetail>(
    `/api/admin/projects/${encodeURIComponent(id)}/unsuspend`,
  );
  return r.data;
}

/**
 * Permanently delete a project — super_admin only. The backend requires an
 * If-Match etag and cascades every child row (players, guilds, chat, help
 * requests, memberships, invites).
 */
export async function deleteProject(id: string, etag: string): Promise<void> {
  await apiClient.delete<void>(
    `/api/admin/projects/${encodeURIComponent(id)}`,
    etag,
  );
}

/** One metric's aggregated 7-day view. Mirrors store.MetricSeries. */
export interface MetricSeries {
  /** All-time count. null for chat — not computed server-side. */
  total: number | null;
  last_7d: number;
  /** 7 daily counts, oldest day first, today last. */
  series: number[];
}

/** Game-level analytics payload. Mirrors store.ProjectAnalytics. */
export interface ProjectAnalytics {
  window_days: number;
  generated_at: string;
  players: MetricSeries;
  guilds: MetricSeries;
  chat: MetricSeries;
}

/**
 * Fetch read-only game-level analytics for a project. Backed by a lazy,
 * Redis-cached endpoint — see docs/superpowers/specs/2026-05-21-admin-analytics-design.md.
 */
export async function getProjectAnalytics(
  id: string,
): Promise<ProjectAnalytics> {
  const r = await apiClient.get<ProjectAnalytics>(
    `/api/admin/projects/${encodeURIComponent(id)}/analytics`,
  );
  return r.data;
}
